import { FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';

export interface FormlyValidationError {
  label: string;
  messages: string[];
}

/**
 * Recursively traverses a formly field tree and collects validation errors
 * from invalid leaf fields. Skips structural types (section, block, formly-group).
 */
export function collectFormlyValidationErrors(
  field: FormlyFieldConfig,
  translate?: TranslateService
): FormlyValidationError[] {
  const errors: FormlyValidationError[] = [];

  const traverse = (f: FormlyFieldConfig): void => {
    if (
      f.type === 'section' ||
      f.type === 'block' ||
      f.type === 'formly-group'
    ) {
      f.fieldGroup?.forEach(traverse);
      return;
    }

    if (f.formControl?.invalid) {
      const label = f.props?.label || f.key?.toString() || '(Unnamed field)';
      const controlErrors = f.formControl.errors ?? {};

      const messages: string[] = Object.keys(controlErrors).map(errKey => {
        const validationMessages = f.validation?.messages ?? {};

        // If there's a custom validation message defined on the field
        if (validationMessages[errKey]) {
          const msgDef = validationMessages[errKey];
          if (typeof msgDef === 'function') {
            const result = msgDef(controlErrors[errKey], f);
            const strResult =
              typeof result === 'object'
                ? JSON.stringify(result)
                : String(result);
            return translate ? translate.instant(strResult) : strResult;
          }
          const strMsgDef = String(msgDef);
          return translate ? translate.instant(strMsgDef) : strMsgDef;
        }

        // The fallback default error message
        let isInvalidStr = 'is invalid';
        let translatedErrKey = errKey;

        if (translate) {
          // Translate "is invalid"
          isInvalidStr = translate.instant('is invalid');

          // Translate the actual error key (e.g: 'required')
          // If translation doesn't exist, it safely returns the raw key
          translatedErrKey = translate.instant(errKey);
        }

        return `${label} ${isInvalidStr} (${translatedErrKey})`;
      });

      errors.push({ label, messages });
    }

    f.fieldGroup?.forEach(traverse);
  };

  traverse(field);
  return errors;
}

/**
 * Collects validation errors from a formly layout array and returns
 * a pipe-delimited summary string suitable for analytics tracking.
 */
export function getFormlyValidationErrorsSummary(
  layout: FormlyFieldConfig[],
  translate?: TranslateService
): string {
  const allErrors: FormlyValidationError[] = [];

  layout.forEach(field => {
    allErrors.push(...collectFormlyValidationErrors(field, translate));
  });

  if (allErrors.length === 0) {
    return translate ? translate.instant('Form is invalid') : 'Form is invalid';
  }

  return allErrors
    .map(err => `${err.label}: ${err.messages.join(', ')}`)
    .join(' | ');
}
